// MARK://dhk8/admin-shell/components/UnitPicker
// Selector ÚNICO de unidades — v2 (Gustavo 2026-06-23). Antes era un <select> sobre
// una lista curada; ahora es un COMBOBOX BUSCABLE sobre un catálogo COMPLETO y
// TIPADO (cero texto libre):
//   · Favoritos    — tenantUnits con isFavorite (claves SAT recurrentes); ★ para quitar.
//   · Personalizadas — tenantUnits con isCustom (cosas fuera del SAT).
//   · Comunes      — la lista operativa curada (Peso / Volumen / Conteo) para el día a día.
//   · Catálogo SAT — c_ClaveUnidad (2,417 claves, LAZY-loaded al teclear); elegir una
//                    llama setUnitFavorite(...favorite:true) para tiparla+reutilizarla.
//   · "＋ Crear unidad personalizada" — modal (label + símbolo? + claveSAT?) → createCustomUnit.
//
// API pública SIN CAMBIOS: value / onChange / label / mode / bare. mode='unit'
// (operativo, default) → favoritos + custom + comunes + búsqueda SAT (value = code/
// clave/slug). mode='sat' (satUnitKey/CFDI) → el dropdown es el catálogo SAT completo
// buscable (value = clave).
//
// `unitShort(value)` / `unitLabel(value)` siguen exportándose (≈13 usos). Resuelven
// primero los códigos curados (SI: 'GR'→'g'), luego favoritos/custom del tenant y el
// símbolo/nombre del catálogo SAT (cargados perezosamente y cacheados a nivel módulo).
// Equivalencias de conversión viven en dockethub_erp.unit_conversions (claves `code`).
import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { ChevronDown, Search, Star, Plus, Pencil } from 'lucide-react'
import * as unitsClient from '../lib/clients/units.js'
import type { TenantUnit } from '../lib/clients/units.js'
import Sheet from './Sheet.js'

type U = { code: string; label: string; short: string; group: string; sat?: string }

// Unidad operativa. code = canónico (alineado a unit_conversions). short = abreviación SI
// para mostrar. sat = clave c_ClaveUnidad.
export const UNITS: U[] = [
  // Peso
  { code: 'MG', label: 'Miligramo (mg)', short: 'mg', group: 'Peso', sat: 'MGM' },
  { code: 'GR', label: 'Gramo (g)', short: 'g', group: 'Peso', sat: 'GRM' },
  { code: 'KG', label: 'Kilogramo (kg)', short: 'kg', group: 'Peso', sat: 'KGM' },
  { code: 'OZ', label: 'Onza (oz)', short: 'oz', group: 'Peso', sat: 'ONZ' },
  { code: 'LB', label: 'Libra (lb)', short: 'lb', group: 'Peso', sat: 'LBR' },
  // Volumen
  { code: 'ML', label: 'Mililitro (ml)', short: 'ml', group: 'Volumen', sat: 'MLT' },
  { code: 'L', label: 'Litro (L)', short: 'L', group: 'Volumen', sat: 'LTR' },
  { code: 'FLOZ', label: 'Onza líquida (fl oz)', short: 'fl oz', group: 'Volumen', sat: 'F18' },
  { code: 'GAL', label: 'Galón (gal)', short: 'gal', group: 'Volumen', sat: 'GLL' },
  { code: 'CDTA', label: 'Cucharadita (tsp)', short: 'cdta', group: 'Volumen' },
  { code: 'CDA', label: 'Cucharada (tbsp)', short: 'cda', group: 'Volumen' },
  { code: 'TZ', label: 'Taza (cup)', short: 'tz', group: 'Volumen' },
  // Conteo
  { code: 'PZA', label: 'Pieza (pza)', short: 'pza', group: 'Conteo', sat: 'H87' },
  { code: 'DOC', label: 'Docena (doc)', short: 'doc', group: 'Conteo', sat: 'DZN' },
  { code: 'PAQ', label: 'Paquete (paq)', short: 'paq', group: 'Conteo', sat: 'XPK' },
  { code: 'BOL', label: 'Bolsa', short: 'bolsa', group: 'Conteo', sat: 'XBG' },
  { code: 'CAJA', label: 'Caja', short: 'caja', group: 'Conteo', sat: 'XBX' },
]

// Claves SAT c_ClaveUnidad "comunes" (mode="sat", sección Comunes). Derivadas del set
// operativo + servicio. El catálogo SAT completo se carga aparte (lazy).
const SAT_UNITS: U[] = [
  ...UNITS.filter((u) => u.sat).map((u) => ({ code: u.sat as string, label: `${u.label.replace(/\s*\(.*\)/, '')} · ${u.sat}`, short: u.sat as string, group: u.group })),
  { code: 'E48', label: 'Unidad de servicio · E48', short: 'E48', group: 'Otros' },
]

// Alias de valores legacy/variantes → código canónico (la data ya está normalizada;
// esto cubre stragglers para que se muestren seleccionados, no fuera de catálogo).
const ALIAS: Record<string, string> = {
  g: 'GR', gr: 'GR', gramo: 'GR', gramos: 'GR', kg: 'KG', kilo: 'KG', kilos: 'KG', mg: 'MG',
  ml: 'ML', mililitro: 'ML', l: 'L', lt: 'L', litro: 'L', litros: 'L',
  oz: 'OZ', lb: 'LB', floz: 'FLOZ', gal: 'GAL', galon: 'GAL', taza: 'TZ', cup: 'TZ',
  cucharada: 'CDA', tbsp: 'CDA', cucharadita: 'CDTA', tsp: 'CDTA',
  pza: 'PZA', pieza: 'PZA', piezas: 'PZA', pz: 'PZA', pieces: 'PZA', piece: 'PZA', pc: 'PZA', pcs: 'PZA',
  caja: 'CAJA', cajas: 'CAJA', docena: 'DOC', doc: 'DOC', paquete: 'PAQ', bolsa: 'BOL',
}

function canonical(value: string, units: U[]): string {
  if (!value) return ''
  if (units.some((u) => u.code === value)) return value
  const a = ALIAS[value.trim().toLowerCase()]
  return a && units.some((u) => u.code === a) ? a : value
}

function groupsOf(units: U[]): string[] {
  return [...new Set(units.map((u) => u.group))]
}

function norm(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

// ── Catálogo SAT completo (lazy / dynamic import) ────────────────────────────
// 2,417 claves → chunk aparte (NO entra al bundle principal). Se carga la primera
// vez que un picker se abre y el usuario teclea, o cuando un display-helper lo pide.
export interface SatUnit { clave: string; nombre: string; simbolo: string | null }
let satCatalog: SatUnit[] | null = null
let satCatalogPromise: Promise<SatUnit[]> | null = null
let satByClave: Map<string, SatUnit> | null = null
const satListeners = new Set<() => void>()
function notifySat() { for (const l of satListeners) l() }

async function loadSatCatalog(): Promise<SatUnit[]> {
  if (satCatalog) return satCatalog
  if (!satCatalogPromise) {
    satCatalogPromise = import('../data/sat-clave-unidad.json')
      .then((m) => {
        const data = ((m as { default?: unknown }).default ?? m) as unknown as SatUnit[]
        satCatalog = data
        satByClave = new Map(data.map((u) => [u.clave, u]))
        notifySat()
        return data
      })
      .catch((err) => { satCatalogPromise = null; throw err })
  }
  return satCatalogPromise
}

// ── Cache de unidades del tenant (favoritos + custom), compartida entre pickers ──
let tenantUnits: TenantUnit[] = []
let tenantUnitsLoaded = false
let tenantUnitsPromise: Promise<TenantUnit[]> | null = null
const tuListeners = new Set<() => void>()
function notifyTu() { for (const l of tuListeners) l() }

function loadTenantUnits(force = false): Promise<TenantUnit[]> {
  if (tenantUnitsLoaded && !force) return Promise.resolve(tenantUnits)
  if (!tenantUnitsPromise || force) {
    tenantUnitsPromise = unitsClient.listTenantUnits()
      .then((list) => { tenantUnits = list; tenantUnitsLoaded = true; notifyTu(); return list })
      .catch((err) => { tenantUnitsPromise = null; throw err })
  }
  return tenantUnitsPromise
}
/** Fuerza recarga de la cache de tenantUnits (tras crear/favoritear desde la página de gestión). */
export function refreshTenantUnits(): Promise<TenantUnit[]> { return loadTenantUnits(true) }

// Hooks que re-renderizan cuando llegan los datos lazy/cacheados.
function useTenantUnits(): TenantUnit[] {
  return useSyncExternalStore(
    (cb) => { tuListeners.add(cb); return () => { tuListeners.delete(cb) } },
    () => tenantUnits,
  )
}
function useSatCatalog(): SatUnit[] | null {
  return useSyncExternalStore(
    (cb) => { satListeners.add(cb); return () => { satListeners.delete(cb) } },
    () => satCatalog,
  )
}

// ── Helpers de DISPLAY (úsalos en cualquier vista que muestre una unidad) ──────
const UNIT_BY_CODE = new Map(UNITS.map((u) => [u.code, u]))
/** Abreviación SI/estándar para mostrar (ej. 'GR' → 'g', 'PZA' → 'pza'). Resuelve, en
 *  orden: código curado → ALIAS legacy → favorito/custom del tenant (symbol/label) →
 *  símbolo del catálogo SAT (si ya está cargado). Fallback: el valor crudo. */
export function unitShort(value: string | null | undefined): string {
  if (!value) return ''
  const c = canonical(value, UNITS)
  const curated = UNIT_BY_CODE.get(c)?.short
  if (curated) return curated
  const tu = tenantUnits.find((u) => u.unitKey === value)
  if (tu) return tu.symbol || tu.label || value
  const sat = satByClave?.get(value)
  if (sat) return sat.simbolo || sat.clave
  return value
}
/** Etiqueta larga (ej. 'GR' → 'Gramo (g)'). Fallback al valor crudo. */
export function unitLabel(value: string | null | undefined): string {
  if (!value) return ''
  const c = canonical(value, UNITS)
  const curated = UNIT_BY_CODE.get(c)?.label
  if (curated) return curated
  const tu = tenantUnits.find((u) => u.unitKey === value)
  if (tu) return tu.label || value
  const sat = satByClave?.get(value)
  if (sat) return `${sat.nombre} · ${sat.clave}`
  return value
}

// ── Tipos de fila del dropdown ───────────────────────────────────────────────
type Row =
  | { kind: 'fav'; code: string; primary: string; secondary: string; tu: TenantUnit }
  | { kind: 'custom'; code: string; primary: string; secondary: string; tu: TenantUnit }
  | { kind: 'common'; code: string; primary: string; secondary: string }
  | { kind: 'sat'; code: string; primary: string; secondary: string }

interface Section { title: string; rows: Row[] }

interface UnitPickerProps {
  value: string
  onChange: (value: string) => void
  label?: string
  /** 'unit' = unidad operativa (default); 'sat' = clave SAT c_ClaveUnidad. */
  mode?: 'unit' | 'sat'
  /** Inline (sin <label> envolvente) para componer en filas; default false. */
  bare?: boolean
}

export default function UnitPicker({ value, onChange, label, mode = 'unit', bare }: UnitPickerProps) {
  const tus = useTenantUnits()
  const sat = useSatCatalog()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [busy, setBusy] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  // Carga datos al montar (tenantUnits siempre; SAT al primer foco/teclear).
  useEffect(() => { void loadTenantUnits().catch(() => { /* sin backend → vacío */ }) }, [])
  useEffect(() => { if (open) void loadSatCatalog().catch(() => { /* chunk no disponible */ }) }, [open])

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const q = norm(query.trim())
  const favorites = useMemo(() => tus.filter((u) => u.isFavorite), [tus])
  const customs = useMemo(() => tus.filter((u) => u.isCustom), [tus])

  // Construye las secciones (en orden) filtradas por la búsqueda.
  const sections = useMemo<Section[]>(() => {
    const matchTu = (u: TenantUnit) => !q || norm(u.label).includes(q) || norm(u.unitKey).includes(q) || (!!u.symbol && norm(u.symbol).includes(q)) || (!!u.satClave && norm(u.satClave).includes(q))
    const out: Section[] = []

    if (mode === 'sat') {
      // mode='sat' → el dropdown es el catálogo SAT completo buscable (value = clave).
      // Comunes SAT primero (sin búsqueda), luego el catálogo completo al teclear.
      if (!q) {
        out.push({ title: 'Comunes', rows: SAT_UNITS.map((u) => ({ kind: 'common', code: u.code, primary: u.code, secondary: u.label.replace(/\s*·.*/, '') })) })
      }
      const satList = sat ?? []
      const satRows: Row[] = (q
        ? satList.filter((u) => norm(u.clave).includes(q) || norm(u.nombre).includes(q) || (!!u.simbolo && norm(u.simbolo).includes(q)))
        : satList
      ).slice(0, q ? 80 : 60).map((u) => ({ kind: 'sat', code: u.clave, primary: u.clave, secondary: u.simbolo ? `${u.nombre} · ${u.simbolo}` : u.nombre }))
      out.push({ title: q ? 'Catálogo SAT' : 'Catálogo SAT (teclea para buscar las 2,417 claves)', rows: satRows })
      return out
    }

    // mode='unit' → favoritos + personalizadas + comunes + búsqueda SAT.
    const favRows = favorites.filter(matchTu).map<Row>((u) => ({ kind: 'fav', code: u.unitKey, primary: u.label, secondary: u.symbol || u.satClave || u.unitKey, tu: u }))
    if (favRows.length) out.push({ title: 'Favoritos', rows: favRows })

    const customRows = customs.filter(matchTu).map<Row>((u) => ({ kind: 'custom', code: u.unitKey, primary: u.label, secondary: u.symbol || 'personalizada', tu: u }))
    if (customRows.length) out.push({ title: 'Personalizadas', rows: customRows })

    const commonRows = UNITS
      .filter((u) => !q || norm(u.label).includes(q) || norm(u.code).includes(q) || norm(u.short).includes(q))
      .map<Row>((u) => ({ kind: 'common', code: u.code, primary: u.label, secondary: u.group }))
    if (commonRows.length) out.push({ title: 'Comunes', rows: commonRows })

    // Catálogo SAT: solo al teclear (lazy). Elegir uno = favoritearlo (tipado+reusable).
    if (q) {
      const satList = sat ?? []
      const seen = new Set(favorites.map((u) => u.unitKey))
      const satRows: Row[] = satList
        .filter((u) => !seen.has(u.clave) && (norm(u.clave).includes(q) || norm(u.nombre).includes(q) || (!!u.simbolo && norm(u.simbolo).includes(q))))
        .slice(0, 60)
        .map((u) => ({ kind: 'sat', code: u.clave, primary: u.nombre, secondary: u.simbolo ? `${u.clave} · ${u.simbolo}` : u.clave }))
      if (satRows.length) out.push({ title: sat ? 'Catálogo SAT' : 'Catálogo SAT…', rows: satRows })
    }
    return out
  }, [mode, q, favorites, customs, sat])

  // Lista plana para navegación con teclado.
  const flat = useMemo(() => sections.flatMap((s) => s.rows), [sections])

  // Texto del input: mientras está abierto editamos `query`; cerrado mostramos la etiqueta del valor.
  const text = open ? query : (value ? displayFor(value, tus, sat) : '')

  function openMenu() {
    setQuery('')
    setActive(0)
    setOpen(true)
  }

  async function pick(row: Row) {
    if (row.kind === 'sat' && mode === 'unit') {
      // Elegir del catálogo SAT en modo operativo = favoritearlo (tipado + reusable).
      setBusy(true)
      try {
        const meta = satByClave?.get(row.code)
        await unitsClient.setUnitFavorite({ unitKey: row.code, label: meta?.nombre ?? row.primary, symbol: meta?.simbolo ?? null, satClave: row.code, favorite: true })
        await refreshTenantUnits().catch(() => {})
      } catch { /* aún así seleccionamos la clave */ }
      finally { setBusy(false) }
    }
    onChange(row.code)
    setOpen(false)
  }

  async function toggleFav(tu: TenantUnit, e: React.MouseEvent) {
    e.stopPropagation()
    setBusy(true)
    try {
      await unitsClient.setUnitFavorite({ unitKey: tu.unitKey, label: tu.label, symbol: tu.symbol, satClave: tu.satClave, favorite: !tu.isFavorite })
      await refreshTenantUnits().catch(() => {})
    } catch { /* no-op */ }
    finally { setBusy(false) }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { openMenu(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') {
      e.preventDefault()
      const row = flat[active]
      if (row) void pick(row)
    } else if (e.key === 'Escape') { e.preventDefault(); setOpen(false) }
  }

  let idx = -1
  const combo = (
    <div className="nui-unit" ref={rootRef}>
      <style>{UNIT_PICKER_CSS}</style>
      <div className={`nui-unit__field${open ? ' is-open' : ''}`}>
        <Search size={16} aria-hidden />
        <input
          className="nui-unit__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          placeholder={mode === 'sat' ? 'Buscar clave SAT (ej. KGM, pieza, litro)…' : 'Buscar unidad (favoritos, SAT, personalizadas)…'}
          value={text}
          onFocusCapture={() => { if (!open) openMenu() }}
          onChange={(e) => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true) }}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="nui-unit__caret" aria-label={open ? 'Cerrar' : 'Abrir lista'} tabIndex={-1} onClick={() => (open ? setOpen(false) : openMenu())}>
          <ChevronDown size={16} aria-hidden />
        </button>
      </div>

      {open && (
        <div className="nui-unit__menu" id={listId} role="listbox">
          {flat.length === 0 && (
            <div className="nui-unit__empty">
              {mode === 'unit' && q ? 'Sin coincidencias. Crea una unidad personalizada abajo.' : 'Sin coincidencias.'}
            </div>
          )}
          {sections.map((sec) => (
            <div key={sec.title} className="nui-unit__group">
              <div className="nui-unit__grouphead">{sec.title}</div>
              {sec.rows.map((row) => {
                idx += 1
                const i = idx
                const selected = row.code === value
                return (
                  <button
                    key={`${row.kind}-${row.code}`}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`nui-unit__opt${i === active ? ' is-active' : ''}${selected ? ' is-selected' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => void pick(row)}
                  >
                    <span className="nui-unit__opt-main">
                      <span className="nui-unit__opt-primary">{row.primary}</span>
                      <span className="nui-unit__opt-secondary">{row.secondary}</span>
                    </span>
                    {row.kind === 'fav' && (
                      <span className="nui-unit__star is-on" aria-label="Quitar de favoritos" title="Quitar de favoritos" onClick={(e) => void toggleFav(row.tu, e)}>
                        <Star size={14} />
                      </span>
                    )}
                    {row.kind === 'custom' && row.tu.isFavorite && (
                      <span className="nui-unit__star is-on" aria-hidden><Star size={14} /></span>
                    )}
                    {row.kind === 'sat' && mode === 'unit' && (
                      <span className="nui-unit__add" aria-hidden title="Agregar a favoritos"><Star size={13} /> favorito</span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
          {mode === 'unit' && (
            <button type="button" className="nui-unit__create" onMouseDown={(e) => e.preventDefault()} onClick={() => { setOpen(false); setCreateOpen(true) }}>
              <Plus size={15} aria-hidden /> Crear unidad personalizada
            </button>
          )}
          {busy && <div className="nui-unit__busy">Guardando…</div>}
        </div>
      )}

      <CreateUnitModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(u) => { setCreateOpen(false); onChange(u.unitKey) }}
      />
    </div>
  )

  if (bare) return combo
  return (
    <label className="adm-field">
      {label !== undefined && <span className="adm-field__label">{label}</span>}
      {combo}
    </label>
  )
}

// Etiqueta a mostrar en el input cuando hay un valor seleccionado (cerrado).
function displayFor(value: string, tus: TenantUnit[], sat: SatUnit[] | null): string {
  const c = canonical(value, UNITS)
  const curated = UNIT_BY_CODE.get(c)
  if (curated) return curated.label
  const tu = tus.find((u) => u.unitKey === value)
  if (tu) return tu.symbol ? `${tu.label} (${tu.symbol})` : tu.label
  const s = (sat ?? []).find((u) => u.clave === value) ?? satByClave?.get(value)
  if (s) return `${s.nombre} · ${s.clave}`
  return value
}

// ── Modal: crear unidad personalizada ────────────────────────────────────────
function CreateUnitModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (u: TenantUnit) => void }) {
  const sat = useSatCatalog()
  const [label, setLabel] = useState('')
  const [symbol, setSymbol] = useState('')
  const [satClave, setSatClave] = useState('')
  const [satQuery, setSatQuery] = useState('')
  const [satOpen, setSatOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { if (open) { setLabel(''); setSymbol(''); setSatClave(''); setSatQuery(''); setSatOpen(false); setError(null); void loadSatCatalog().catch(() => {}) } }, [open])

  const satMatches = useMemo(() => {
    const qq = norm(satQuery.trim())
    if (!qq || !sat) return [] as SatUnit[]
    return sat.filter((u) => norm(u.clave).includes(qq) || norm(u.nombre).includes(qq)).slice(0, 20)
  }, [satQuery, sat])

  async function save() {
    setError(null)
    if (!label.trim()) { setError('El nombre de la unidad es obligatorio.'); return }
    setSaving(true)
    try {
      const u = await unitsClient.createCustomUnit({
        label: label.trim(),
        ...(symbol.trim() ? { symbol: symbol.trim() } : {}),
        ...(satClave.trim() ? { satClave: satClave.trim() } : {}),
      })
      await refreshTenantUnits().catch(() => {})
      onCreated(u)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear la unidad.')
    } finally { setSaving(false) }
  }

  return (
    <Sheet
      open={open}
      title="Crear unidad personalizada"
      onClose={onClose}
      footer={<>
        <button type="button" className="adm-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="adm-btn adm-btn--primary" onClick={() => void save()} disabled={saving || !label.trim()}>{saving ? 'Guardando…' : 'Crear'}</button>
      </>}
    >
      <div className="adm-sheet__body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--pos-space-3, 12px)' }}>
        {error && <div className="adm-banner adm-banner--danger" role="alert">{error}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Nombre</span>
          <input className="adm-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Charola, rejilla, atado…" autoFocus />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Símbolo (opcional)</span>
          <input className="adm-input" value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="ch, rej…" />
        </label>
        <div className="adm-field" style={{ position: 'relative' }}>
          <span className="adm-field__label">Clave SAT equivalente (opcional)</span>
          <input
            className="adm-input"
            value={satOpen ? satQuery : satClave}
            onChange={(e) => { setSatQuery(e.target.value); setSatClave(e.target.value); setSatOpen(true) }}
            onFocus={() => { setSatQuery(satClave); setSatOpen(true) }}
            placeholder="Busca una clave c_ClaveUnidad (opcional)"
          />
          {satOpen && satMatches.length > 0 && (
            <div className="nui-unit__menu" role="listbox" style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4 }}>
              {satMatches.map((u) => (
                <button
                  key={u.clave}
                  type="button"
                  className="nui-unit__opt"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { setSatClave(u.clave); setSatQuery(u.clave); setSatOpen(false) }}
                >
                  <span className="nui-unit__opt-main">
                    <span className="nui-unit__opt-primary">{u.clave}</span>
                    <span className="nui-unit__opt-secondary">{u.nombre}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Sheet>
  )
}

// Re-export del tipo + helpers de gestión para la página de catálogo de unidades.
export type { TenantUnit }
export { SAT_UNITS, canonical, norm, loadSatCatalog, loadTenantUnits, useSatCatalog, useTenantUnits }

const UNIT_PICKER_CSS = `
.nui-unit { position: relative; }
.nui-unit__field {
  display: flex; align-items: center; gap: var(--pos-space-2, 8px);
  min-height: 44px; padding: 0 var(--pos-space-3, 12px);
  border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md, 8px);
  background: var(--pos-canvas); color: var(--pos-fg);
}
.nui-unit__field.is-open { border-color: var(--pos-tint); box-shadow: 0 0 0 3px var(--pos-tint-soft); }
.nui-unit__field svg { flex-shrink: 0; color: var(--pos-fg-tertiary); }
.nui-unit__input {
  flex: 1; min-width: 0; border: none; outline: none; background: transparent;
  color: var(--pos-fg); font-family: var(--font-sans); font-size: var(--pos-text-sm, 13px);
}
.nui-unit__caret { display: inline-flex; padding: 2px; border: none; background: transparent; color: var(--pos-fg-tertiary); cursor: pointer; }
.nui-unit__menu {
  position: absolute; z-index: var(--pos-z-sheet, 200);
  top: calc(100% + 4px); left: 0; right: 0;
  max-height: 320px; overflow-y: auto;
  background: var(--pos-surface); color: var(--pos-fg);
  border: 1px solid var(--pos-separator-strong);
  border-radius: var(--pos-radius-md, 8px);
  box-shadow: var(--pos-shadow-3);
  padding: var(--pos-space-1, 4px);
}
.nui-unit__group + .nui-unit__group { border-top: 1px solid var(--pos-separator); margin-top: 2px; padding-top: 2px; }
.nui-unit__grouphead {
  padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px) 2px;
  font-family: var(--font-display, var(--font-sans));
  font-size: var(--pos-text-xxs, 11px); font-weight: var(--pos-weight-semibold, 600);
  letter-spacing: var(--pos-tracking-widest); text-transform: uppercase; color: var(--pos-fg-tertiary);
}
.nui-unit__opt {
  display: flex; align-items: center; gap: var(--pos-space-2, 8px);
  width: 100%; text-align: left; cursor: pointer;
  padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px);
  border: none; border-radius: var(--pos-radius-sm, 6px);
  background: transparent; color: var(--pos-fg); font-family: var(--font-sans);
}
.nui-unit__opt:hover, .nui-unit__opt.is-active { background: var(--pos-tint-soft); }
.nui-unit__opt.is-selected { background: var(--pos-tint-fill); color: var(--pos-tint-on); }
.nui-unit__opt-main { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1; }
.nui-unit__opt-primary { font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-medium, 500); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nui-unit__opt-secondary { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nui-unit__star { display: inline-flex; align-items: center; padding: 4px; border-radius: var(--pos-radius-sm, 6px); color: var(--pos-fg-tertiary); cursor: pointer; flex-shrink: 0; }
.nui-unit__star.is-on { color: var(--pos-warning, #d4a017); }
.nui-unit__star:hover { background: var(--pos-elevated); }
.nui-unit__add { display: inline-flex; align-items: center; gap: 3px; font-size: var(--pos-text-xxs, 11px); color: var(--pos-fg-tertiary); flex-shrink: 0; }
.nui-unit__empty { padding: var(--pos-space-3, 12px); font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary); text-align: center; }
.nui-unit__busy { padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px); font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary); text-align: center; }
.nui-unit__create {
  display: flex; align-items: center; gap: var(--pos-space-2, 8px); width: 100%;
  margin-top: 2px; padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px);
  border: none; border-top: 1px solid var(--pos-separator); border-radius: 0 0 var(--pos-radius-sm, 6px) var(--pos-radius-sm, 6px);
  background: transparent; color: var(--pos-tint); font-family: var(--font-sans);
  font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-medium, 500); cursor: pointer; text-align: left;
}
.nui-unit__create:hover { background: var(--pos-tint-soft); }
`
